/* LuzLed header: products panel (hover with a short delay, click, keyboard), phone menu sheet, scroll state,
   bars that stick under it ([data-luz-stick]), quote list count. */
class LuzHeader extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector("[data-lhd-panel]");
    this.opener = this.querySelector("[data-lhd-open]");
    this.sheet = this.querySelector("[data-lhd-sheet-panel]");
    this.burger = this.querySelector("[data-lhd-sheet]");
    /* The search key cap names the Mac key on Apple devices. */
    if (/Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)) {
      this.querySelectorAll("[data-lhd-kbd]").forEach((k) => (k.textContent = "⌘K"));
      this.querySelectorAll("[data-lhd-search-btn]").forEach((b) => b.setAttribute("aria-label", "Search products, ⌘K"));
    }
    const hover = matchMedia("(hover: hover) and (min-width: 990px)");
    let openTimer, closeTimer;

    if (this.panel && this.opener) {
      const zone = [this.opener.closest("li"), this.panel];
      const enter = () => {
        if (!hover.matches) return;
        clearTimeout(closeTimer);
        openTimer = setTimeout(() => this.setPanel(true), this.isOpen() ? 0 : 140);
      };
      const leave = () => {
        if (!hover.matches) return;
        clearTimeout(openTimer);
        closeTimer = setTimeout(() => this.setPanel(false), 260);
      };
      zone.forEach((el) => { el.addEventListener("pointerenter", enter); el.addEventListener("pointerleave", leave); });
      this.opener.addEventListener("click", () => { clearTimeout(openTimer); this.setPanel(!this.isOpen()); });
      this.addEventListener("focusout", (e) => {
        if (this.isOpen() && !zone.some((el) => el.contains(e.relatedTarget))) this.setPanel(false);
      });
      /* Keyboard: the panel sits apart from its button in the page, so Tab from the open button goes into the panel,
         Shift Tab from its first link back to the button, and Tab from its last link on to the next menu item
         (disclosure navigation pattern; audit 2026-10-11). */
      const links = () => [...this.panel.querySelectorAll("a[href], button")].filter((el) => el.offsetParent);
      const after = () => {
        const all = [...this.querySelectorAll(".lhd-item")];
        return all[all.indexOf(this.opener) + 1];
      };
      this.addEventListener("keydown", (e) => {
        if (e.key !== "Tab" || !this.isOpen()) return;
        const l = links();
        if (!l.length) return;
        if (document.activeElement === this.opener && !e.shiftKey) { e.preventDefault(); l[0].focus(); }
        else if (document.activeElement === l[0] && e.shiftKey) { e.preventDefault(); this.opener.focus(); }
        else if (document.activeElement === l.at(-1) && !e.shiftKey && after()) { e.preventDefault(); this.setPanel(false); after().focus(); }
      });
    }

    this.burger?.addEventListener("click", () => this.setSheet(this.burger.getAttribute("aria-expanded") !== "true"));
    this.initQuote();
    this.querySelector("[data-lhd-scrim]").addEventListener("click", () => this.closeAll());
    this.querySelector("[data-luz-search-open]")?.addEventListener("click", () => this.closeAll());
    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      if (this.quoteOpen()) { this.setQuote(false); this.quoteLink.focus(); }
      if (this.isOpen()) { this.setPanel(false); this.opener.focus(); }
      if (this.burger?.getAttribute("aria-expanded") === "true") { this.setSheet(false); this.burger.focus(); }
    });
    matchMedia("(min-width: 990px)").addEventListener("change", () => this.closeAll());

    const sticks = [...document.querySelectorAll("[data-luz-stick]")];
    const onScroll = () => {
      this.dataset.state = scrollY > 6 ? "scrolled" : "top";
      if (!sticks.length) return;
      const line = this.querySelector(".lhd-wrap").offsetHeight + 1;
      sticks.forEach((el) => el.classList.toggle("is-stuck", getComputedStyle(el).position === "sticky" && el.getBoundingClientRect().top <= line));
    };
    addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    /* The count and the drop down follow the quote list kept in the browser (assets/luz-list.js, request 185). */
    document.addEventListener("luz-list:update", () => this.renderQuote());
    addEventListener("pageshow", (e) => { if (e.persisted) this.renderQuote(); });
    this.renderQuote();
  }

  isOpen() { return this.panel?.classList.contains("is-open"); }

  setPanel(open) {
    if (!this.panel || open === this.isOpen()) return;
    this.panel.classList.toggle("is-open", open);
    this.opener.setAttribute("aria-expanded", String(open));
    this.classList.toggle("is-open", open);
  }

  setSheet(open) {
    if (!this.sheet) return;
    this.sheet.classList.toggle("is-open", open);
    this.burger.setAttribute("aria-expanded", String(open));
    this.burger.setAttribute("aria-label", open ? "Close menu" : "Menu");
    this.classList.toggle("is-open", open);
    document.documentElement.classList.toggle("lhd-lock", open);
  }

  closeAll() { this.setPanel(false); this.setSheet(false); this.setQuote?.(false); }

  /* Desktop quote list drop down (request 101): opens from the header icon, lists the products with quantity and
     remove, and leads to the full list to send. The lines are the list kept in the browser (assets/luz-list.js), with
     our names and codes, never the store titles. Phones and tablets follow the link to the page. */
  initQuote() {
    this.qpanel = this.querySelector("[data-lhd-qpanel]");
    this.quoteLink = this.querySelector("[data-lhd-quote]");
    if (!this.qpanel || !this.quoteLink) return;
    const wide = matchMedia("(min-width: 990px)");
    this.quoteLink.addEventListener("click", (e) => {
      if (!wide.matches || location.pathname === "/cart") return;
      e.preventDefault();
      this.setQuote(!this.quoteOpen());
    });
    document.addEventListener("click", (e) => {
      if (this.quoteOpen() && !e.target.closest("[data-lhd-qpanel],[data-lhd-quote]")) this.setQuote(false);
    });
    this.qpanel.addEventListener("click", (e) => {
      const b = e.target.closest("[data-q-step],[data-q-del]");
      if (!b) return;
      const line = b.closest("[data-v]");
      const q = +line.dataset.qty;
      const next = b.dataset.qDel !== undefined ? 0 : Math.min(window.LuzLimits.qty, Math.max(0, q + +b.dataset.qStep));  // the most of a product (theme settings)
      if (next !== q) window.LuzList.set(line.dataset.v, next, "luz-header");
    });
  }

  quoteOpen() { return this.qpanel?.classList.contains("is-open"); }

  setQuote(open) {
    if (!this.qpanel || open === this.quoteOpen()) return;
    if (open) this.setPanel(false);
    this.qpanel.classList.toggle("is-open", open);
    this.quoteLink.setAttribute("aria-expanded", String(open));
    this.classList.toggle("is-open", open);
  }

  /* The lines, drawn from the list (assets/luz-list.js); the panel is filled on every change, open or not, so it
     never opens stale. */
  renderQuote() {
    const lines = window.LuzList?.lines() || [];
    this.setCount(lines.length);
    if (!this.qpanel) return;
    const e = (t) => String(t ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
    const max = window.LuzLimits.qty;
    const root = window.Shopify?.routes?.root || "/";
    const noimg = '<svg class="lhd-q-ph" viewBox="0 0 48 48" aria-hidden="true"><path d="M24 6v6M9.5 12.5l4 4M38.5 12.5l-4 4M16 30a8 8 0 1 1 16 0c0 3-2 4.5-3 6.5h-10c-1-2-3-3.5-3-6.5ZM19 41h10"/></svg>';
    this.qpanel.querySelector("[data-lhd-q-list]").innerHTML = lines.map((l) => {
      const href = e(l.u || `${root}search?q=${encodeURIComponent(l.c || "")}`);
      return `<div class="lhd-q-line" data-v="${+l.v}" data-qty="${+l.q}">` +
        `<a class="lhd-q-img" href="${href}" tabindex="-1" aria-hidden="true">${l.i ? `<img src="${e(l.i)}" alt="" width="64" height="64" crossorigin="anonymous">` : noimg}</a>` +
        `<div class="lhd-q-b"><a href="${href}">${e(l.n)}</a><small>${e(l.c)}${l.o ? ` · ${e(l.o)}` : ""}</small>` +
        `<div class="lhd-q-qty"><button type="button" data-q-step="-1" aria-label="Less">&minus;</button><span>${+l.q}</span>` +
        `<button type="button" data-q-step="1" aria-label="More"${l.q >= max ? " disabled" : ""}>+</button></div></div>` +
        `<button type="button" class="lhd-q-del" data-q-del aria-label="Remove ${e(l.n)}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg></button></div>`;
    }).join("");
    const n = lines.length;
    this.qpanel.querySelector("[data-lhd-q-n]").textContent = n ? `${n} ${n === 1 ? "product" : "products"}` : "";
    this.qpanel.querySelector("[data-lhd-q-empty]").hidden = n > 0;
    this.qpanel.querySelector("[data-lhd-q-foot]").hidden = n === 0;
  }

  setCount(n) {
    const badge = this.querySelector("[data-lhd-count]");
    const label = this.querySelector("[data-lhd-quote-label]");
    badge.textContent = n;
    badge.hidden = n === 0;
    label.textContent = `Quote list, ${n} ${n === 1 ? "product" : "products"}`;
  }
}
customElements.define("luz-header", LuzHeader);

/* Product photos (request 103): a cut out on white keeps its margins and blends into the grey frame; a photo with
   its own background (a room, a ceiling) fills the frame instead of sitting in it like a pasted rectangle. The
   check reads the photo's outermost pixels once it has loaded (the image CDN allows it), so new products need
   nothing. Only the outer 2% is read: product photos keep a 7% white margin (tools/luz_images.py), so a product near
   its margin never reads as a background (request 166: a deeper ring zoomed such photos and cramped them). */
(() => {
  const FRAMES = ".lc-img, .lp-stage, .lp-card-img, .lsx-img, .lhd-thumb, .lq-img, .lh-g-img, .lx-img, .lhd-q-img";
  const c = document.createElement("canvas");
  c.width = c.height = 48;
  const g = c.getContext("2d", { willReadFrequently: true });
  const check = (img) => {
    const frame = img.closest(FRAMES);
    if (!frame || !img.naturalWidth || img.dataset.fit === img.currentSrc) return;
    img.dataset.fit = img.currentSrc;
    const ratio = img.naturalWidth / img.naturalHeight;
    if (ratio > 1.8 || ratio < 0.55) { frame.classList.remove("is-scene"); return; } /* long products keep their margins */
    try {
      g.clearRect(0, 0, 48, 48);
      g.drawImage(img, 0, 0, 48, 48);
      const d = g.getImageData(0, 0, 48, 48).data;
      let edge = 0, light = 0;
      for (let y = 0; y < 48; y++) for (let x = 0; x < 48; x++) {
        if (x > 0 && x < 47 && y > 0 && y < 47) continue;
        const i = (y * 48 + x) * 4;
        edge++;
        if (d[i + 3] < 20 || Math.min(d[i], d[i + 1], d[i + 2]) >= 222) light++;
      }
      frame.classList.toggle("is-scene", light / edge < 0.8);
    } catch {}
  };
  const scan = () => document.querySelectorAll(`:is(${FRAMES}) img[crossorigin]`).forEach((img) => { if (img.complete) check(img); });
  addEventListener("DOMContentLoaded", scan);
  /* Every later image load (lazy images, a new photo on the stage, search results) is checked as it arrives. */
  document.addEventListener("load", (e) => { if (e.target.tagName === "IMG" && e.target.hasAttribute("crossorigin")) check(e.target); }, true);
})();
